import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Box } from '@mui/material';
import { ScrollContainerProvider } from './ScrollContainer';

interface TabContainerProps {
  /** Whether this tab is currently the active/visible tab. */
  visible: boolean;
  children: React.ReactNode;
}

/**
 * Per-tab scroll container for the keep-alive bottom-nav tabs.
 *
 * Each tab gets its own `position: fixed; overflow-y: auto` element. Browsers
 * preserve overflow elements' `scrollTop` across `display: none/block`, so tabs
 * keep their scroll position natively when switched — no save/restore code
 * needed, no fights with WKWebView's deferred layout.
 *
 * Children inside read the scroll element via `useScrollContainer()` instead of
 * `window.scrollY`.
 */
const TabContainer: React.FC<TabContainerProps> = ({ visible, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Track the live element so children get a stable, non-null value once
  // mounted. The ref is populated during render, but consumers reading from
  // context only re-evaluate when the provider's value changes, so we promote
  // the ref to state after mount.
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    setEl(ref.current);
  }, []);

  // Explicit scrollTop save/restore around display:none. Most browsers preserve
  // scrollTop on overflow:auto across visibility toggles, but WKWebView
  // (Capacitor iOS) can drop it when the element is hidden. To avoid racing
  // the reset, we capture the scrollTop continuously via a passive scroll
  // listener (always up-to-date by the time hide kicks in) and re-apply it
  // synchronously in useLayoutEffect when the tab becomes visible again.
  const savedScrollTopRef = useRef(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onScroll = () => {
      // Only track while visible — scroll events should never fire while
      // display:none, but the guard makes the contract explicit.
      if (visible) savedScrollTopRef.current = node.scrollTop;
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, [visible]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || !visible) return;
    // Restore before paint. Direct property write (not scrollTo) sidesteps
    // WKWebView's deferred-layout pipeline.
    if (node.scrollTop !== savedScrollTopRef.current) {
      node.scrollTop = savedScrollTopRef.current;
    }
  }, [visible]);

  return (
    <Box
      ref={ref}
      data-tab-container={visible ? 'active' : 'inactive'}
      sx={{
        display: visible ? 'block' : 'none',
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        // Intentionally NOT using `-webkit-overflow-scrolling: touch`. Modern
        // WKWebView (iOS 13+) handles momentum scrolling natively on
        // overflow:auto, and the legacy property can detach the scroll layer
        // when display:none is applied, dropping the restored scrollTop.
        // The bottom nav floats above with zIndex 1200, so the container stays
        // beneath it at the default stacking level.
      }}
    >
      <ScrollContainerProvider value={el}>
        {/* Bottom padding so the last items aren't hidden behind the floating
            bottom nav on mobile. Desktop has a side bar instead, so no clearance
            is needed there. */}
        <Box sx={{ pb: { xs: 'calc(88px + var(--safe-area-inset-bottom))', md: 0 } }}>
          {children}
        </Box>
      </ScrollContainerProvider>
    </Box>
  );
};

export default TabContainer;
